import React, { useState, useRef, useEffect } from 'react';
import { StaticImage } from "gatsby-plugin-image";
import { Link, navigate } from "gatsby";

import { ReactComponent as FacebookIcon } from "../images/icons/facebook.svg";
import { ReactComponent as TwitterIcon } from "../images/icons/twitter.svg";
import { ReactComponent as LinkedInIcon } from "../images/icons/linkedin.svg";
import { ReactComponent as MailIcon } from "../images/icons/mail.svg";
import { ReactComponent as CartIcon } from "../images/icons/cart.svg";
import { ReactComponent as FavoriteIcon } from "../images/icons/favorite.svg";
import { ReactComponent as ListCategoryIcon } from "../images/icons/list-category.svg";
import { ReactComponent as BookOpen } from "../images/icons/book-open.svg";
import { ReactComponent as TruckIcon } from "../images/icons/truck.svg";
import { ReactComponent as UserIcon } from "../images/icons/user.svg";
import { ReactComponent as ArrowDownIcon } from "../images/icons/arrow-down.svg";

import MegaMenu from "./MegaMenu";
import SearchDropdown from "./SearchDropdown";
import MobileSearchPanel from "./MobileSearchPanel";
import useAuth from "../hooks/useAuth";
import { useCart } from "../contexts/CartContext";
import { MEGA_MENU_CLOSE_EVENT } from "../utils/megaMenuEvents";
import {
  resolveBrandPath,
  SHOP_ALL_PRODUCTS_PATH,
} from "../utils/brandRoutes";

const SOCIAL_LINKS = {
  facebook: process.env.GATSBY_SOCIAL_FACEBOOK || "https://www.facebook.com/MultiquipUk",
  linkedin: process.env.GATSBY_SOCIAL_LINKEDIN || "https://www.linkedin.com/company/multiquip-uk-limited",
  twitter: process.env.GATSBY_SOCIAL_TWITTER || "https://x.com/MultiquipUK",
};

const PRODUCT_CATALOGUE_URL = "https://multiquip.seeksocial.site/wp-content/uploads/2026/05/MQ-PRODUCT-CATALOGUE-202627-V7-PRINT-READY.pdf";

const Header = ({ isMenuOpen, setIsMenuOpen }) => {
  const { loggedIn } = useAuth();
  const { getTotalItems } = useCart();
  const cartCount = getTotalItems() || 0;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSearchBrand, setSelectedSearchBrand] = useState("all-brands");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const categoryMenuRef = useRef(null);
  const megaMenuRef = useRef(null);
  const accountLinkPath = loggedIn ? "/account" : "/sign-in";
  const accountLinkLabel = loggedIn ? "View Account" : "Sign In/Register";
  const toggleMobileSearchVisibility = () => {
    setIsMobileSearchOpen((previousState) => !previousState);
  };
  const onSearchSubmit = () => {
    const trimmedSearchQuery = searchQuery.trim();
    const isAllBrands = selectedSearchBrand === "all-brands";
    const basePath = isAllBrands
      ? SHOP_ALL_PRODUCTS_PATH
      : resolveBrandPath(selectedSearchBrand);
    const urlSearchParams = new URLSearchParams();

    if (trimmedSearchQuery) {
      urlSearchParams.set("search", trimmedSearchQuery);
    }

    const searchParams = urlSearchParams.toString();
    const searchTargetPath = searchParams ? `${basePath}?${searchParams}` : basePath;
    navigate(searchTargetPath);
    setIsMobileSearchOpen(false);
  };

  const onSearchInputKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    onSearchSubmit();
  };

  // Close category menu for any outside interactions while open.
  useEffect(() => {
    const isInsideMenuControls = (target) => {
      if (!(target instanceof Node)) {
        return false;
      }

      const isInsideCategoryButton =
        categoryMenuRef.current && categoryMenuRef.current.contains(target);
      const isInsideMegaMenu =
        megaMenuRef.current && megaMenuRef.current.contains(target);

      return Boolean(isInsideCategoryButton || isInsideMegaMenu);
    };

    const handleOutsideInteraction = (event) => {
      if (!isInsideMenuControls(event.target)) {
        setIsCategoryMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsCategoryMenuOpen(false);
        return;
      }

      if (!isInsideMenuControls(event.target)) {
        setIsCategoryMenuOpen(false);
      }
    };

    if (isCategoryMenuOpen) {
      const interactionEvents = [
        "pointerdown",
        "click",
        "focusin",
        "submit",
        "touchstart",
      ];

      interactionEvents.forEach((eventName) => {
        document.addEventListener(eventName, handleOutsideInteraction, true);
      });
      document.addEventListener("keydown", handleKeyDown, true);

      return () => {
        interactionEvents.forEach((eventName) => {
          document.removeEventListener(eventName, handleOutsideInteraction, true);
        });
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    }
  }, [isCategoryMenuOpen]);

  useEffect(() => {
    const handleMegaMenuCloseRequest = () => {
      setIsCategoryMenuOpen(false);
    };

    window.addEventListener(MEGA_MENU_CLOSE_EVENT, handleMegaMenuCloseRequest);
    return () => {
      window.removeEventListener(MEGA_MENU_CLOSE_EVENT, handleMegaMenuCloseRequest);
    };
  }, []);

  return (
    <header className="fixed w-full top-0 left-0 right-0 z-50">
      {/* DESKTOP: Top Navigation - Hidden on mobile/tablet */}
      <div className="hidden desktop-lg:flex w-full items-center justify-center bg-mqred5 py-2 container-full">
        <div className="w-full max-w-[1136px] flex items-center justify-between h-[40px]">
          {/* Social Links */}
          <div className="flex items-center gap-[16px]">
            <a
              className="cursor-pointer"
              title="Visit us at Facebook"
              href={SOCIAL_LINKS.facebook}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FacebookIcon className="w-[16px] h-[16px] fill-white hover:fill-mqred3 transition-colors" />
            </a>
            <a
              className="cursor-pointer"
              title="Visit us at LinkedIn"
              href={SOCIAL_LINKS.linkedin}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkedInIcon className="w-[16px] h-[16px] fill-white hover:fill-mqred3 transition-colors" />
            </a>
            <a
              className="cursor-pointer"
              title="Visit us at Twitter/X"
              href={SOCIAL_LINKS.twitter}
              target="_blank"
              rel="noopener noreferrer"
            >
              <TwitterIcon className="w-[16px] h-[16px] fill-white hover:fill-mqred3 transition-colors" />
            </a>
            <a className="cursor-pointer" title="Contact us instead" href="/contact">
              <MailIcon className="w-[16px] h-[16px]  hover:stroke-mqred3 transition-colors" />
            </a>
          </div>

          {/* Top Navigation Menu */}
          <nav className="flex items-center gap-[2px] [&_a]:hover:no-underline">
            <Link to="/about" className="bg-black text-white text-_1424 font-work-sans font-medium p-2 hover:bg-white hover:text-black transition-colors">
              About Multiquip UK
            </Link>
            <span className="text-white text-_1424 font-work-sans">.</span>
            <Link to="/hire" className="bg-black text-white text-_1424 font-work-sans font-medium p-2 hover:bg-white hover:text-black transition-colors">
              MQ Hire
            </Link>
            <span className="text-white text-_1424 font-work-sans">.</span>
            <Link to="/knowledge-base" className="bg-black text-white text-_1424 font-work-sans font-medium p-2 hover:bg-white hover:text-black transition-colors">
              Knowledge Hub
            </Link>
            <span className="text-white text-_1424 font-work-sans">.</span>
            <Link to="/contact" className="bg-black text-white text-_1424 font-work-sans font-medium p-2 hover:bg-white hover:text-black transition-colors">
              Contact
            </Link>
            <span className="text-white text-_1424 font-work-sans">.</span>
            <Link to="/blog-listing" className="bg-black text-white text-_1424 font-work-sans font-medium p-2 hover:bg-white hover:text-black transition-colors">
              Blogs
            </Link>
          </nav>
        </div>
      </div>

      {/* MAIN HEADER - DESKTOP & MOBILE */}
      <div className="w-full h-full bg-mqred7 flex items-center justify-center tablet:py-6 py-0 container-full">
        <div className="w-full max-w-[1136px] tablet:h-[80px] tablet-lg:h-[96px] desktop-lg:px-4 py-[8px]">
          {/* Mobile: Left-aligned logo with right-side actions */}
          <div className="flex w-full h-full items-center justify-between tablet:hidden">
            <div className="flex items-center gap-6 mobile:gap-10">
              <button
                className="text-white hover:text-mqred3 transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                <svg className="w-[24px] h-[24px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <Link to="/">
                <div className="w-[48px] h-[40px] flex items-center justify-center">
                  <StaticImage
                    src="../images/logo.webp"
                    alt="Multiquip UK Logo"
                    className="w-[48px] h-[40px]"
                    objectFit="cover"
                  />
                </div>
              </Link>
            </div>

            <div className="flex items-center gap-6">
              <button
                className="w-[32px] h-[32px] flex items-center justify-center text-white hover:text-mqred3 transition-colors"
                aria-label="Search"
                type="button"
                onClick={toggleMobileSearchVisibility}
              >
                <svg className="w-[24px] h-[24px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              <Link
                to="/account/wishlist"
                className="w-[32px] h-[32px] flex items-center justify-center text-white hover:text-mqred3 transition-colors"
                aria-label="Wishlist"
              >
                <FavoriteIcon className="w-[24px] h-[24px] fill-none stroke-current" />
              </Link>

              <Link
                to="/cart"
                className="relative w-[32px] h-[32px] flex items-center justify-center text-white hover:text-mqred3 transition-colors"
                aria-label="Shopping Cart"
              >
                <CartIcon className="w-[24px] h-[24px] fill-none stroke-current" />
                <span className="absolute -top-1 -right-1 bg-mqred5 text-white text-_1218 font-medium rounded-full w-[20px] h-[20px] flex items-center justify-center">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              </Link>
            </div>
          </div>

          {/* Tablet to Desktop-lg: Centered logo */}
          <div className="hidden w-full tablet:grid desktop-lg:hidden grid-cols-3 items-center">
            <button
              className="justify-self-start text-white hover:text-mqred3 transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-[24px] h-[24px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <Link to="/" className="justify-self-center">
              <div className="w-[91px] h-[76px] flex items-center justify-center">
                <StaticImage
                  src="../images/logo.webp"
                  alt="Multiquip UK Logo"
                  className="w-[91px] h-[76px]"
                  objectFit="cover"
                />
              </div>
            </Link>

            <div className="flex items-center justify-self-end gap-6">
              <button
                className="w-[32px] h-[32px] flex items-center justify-center text-white hover:text-mqred3 transition-colors"
                aria-label="Search"
                type="button"
                onClick={toggleMobileSearchVisibility}
              >
                <svg className="w-[24px] h-[24px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              <Link
                to="/account/wishlist"
                className="w-[32px] h-[32px] flex items-center justify-center text-white hover:text-mqred3 transition-colors"
                aria-label="Wishlist"
              >
                <FavoriteIcon className="w-[24px] h-[24px] fill-none stroke-current" />
              </Link>

              <Link
                to="/cart"
                className="relative w-[32px] h-[32px] flex items-center justify-center text-white hover:text-mqred3 transition-colors"
                aria-label="Shopping Cart"
              >
                <CartIcon className="w-[24px] h-[24px] fill-none stroke-current" />
                <span className="absolute -top-1 -right-1 bg-mqred5 text-white text-_1218 font-medium rounded-full w-[20px] h-[20px] flex items-center justify-center">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              </Link>
            </div>
          </div>

          {/* Desktop-lg+ */}
          <div className="hidden w-full desktop-lg:flex items-center justify-between gap-10">
            <div className="flex items-center gap-4">
              {/* Mobile/Tablet Menu Toggle */}
              <button
                className="tablet-lg:hidden text-white hover:text-mqred3 transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                <svg className="w-[24px] h-[24px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              {/* Left: Logo */}
              <Link to="/" className="flex-shrink-0">
                <div className="w-[91px] h-[76px] flex items-center justify-center">
                  <StaticImage
                    src="../images/logo.webp"
                    alt="Multiquip UK Logo"
                    className="w-[91px] h-[76px]"
                    objectFit="cover"
                  />
                </div>
              </Link>
            </div>

            {/* Right: Search + Action Icons */}
            <div className="flex flex-1 justify-end items-center gap-10">
              <div className="w-full flex justify-end items-center min-w-0 gap-2">
                <div className="w-full max-w-[520px] desktop-xl:max-w-[580px] flex items-center min-w-0 gap-0">
                  <input
                    type="text"
                    placeholder="What are you looking for?"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={onSearchInputKeyDown}
                    className="flex-1 focus:shadow-none focus:border-white h-[48px] px-4 rounded-[8px] rounded-r-none bg-white text-_1424 font-work-sans font-normal placeholder-mqgrey3 min-w-0"
                  />
                  <div className="w-[180px] -ml-2 relative z-10">
                    <SearchDropdown
                      value={selectedSearchBrand}
                      onChange={setSelectedSearchBrand}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={onSearchSubmit}
                  className="px-6 py-3 bg-mqgrey4 text-white text-_1424 font-work-sans font-medium rounded-[8px] hover:bg-mqgrey5 transition-colors whitespace-nowrap"
                >
                  Search
                </button>
              </div>

              {/* Wishlist Icon */}
              <Link
                to="/account/wishlist"
                className="w-[32px] h-[32px] flex items-center justify-center text-white hover:text-mqred3 transition-colors"
              >
                <FavoriteIcon className="w-[24px] h-[24px] fill-none stroke-current" />
              </Link>

              {/* Cart Icon with Badge */}
              <Link
                to="/cart"
                className="relative w-[32px] h-[32px] flex items-center justify-center text-white hover:text-mqred3 transition-colors"
              >
                <CartIcon className="w-[24px] h-[24px] fill-none stroke-current" />
                <span className="absolute -top-1 -right-1 bg-mqred5 text-white text-_1218 font-medium rounded-full w-[20px] h-[20px] flex items-center justify-center">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet Navigation Menu - Tabbed Sidebar */}
        {/* Sidebar menu moved to index.js */}
      </div>

      {/* MOBILE/TABLET: Search Row */}
      <div
        className={`desktop-lg:hidden absolute top-full left-0 right-0 w-full bg-mqred7 shadow-shadow-3 z-40 transform transition-all duration-300 ease-out ${
          isMobileSearchOpen
            ? 'translate-y-0 opacity-100 pointer-events-auto'
            : '-translate-y-2 opacity-0 pointer-events-none'
        }`}
      >
        <div className="w-full max-w-[1136px] mx-auto px-4 py-3 tablet:px-6">
          <MobileSearchPanel
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            selectedSearchBrand={selectedSearchBrand}
            onSelectedSearchBrandChange={setSelectedSearchBrand}
            onSearchInputKeyDown={onSearchInputKeyDown}
            onSearchSubmit={onSearchSubmit}
          />

          <div className="hidden tablet:flex desktop-lg:hidden items-center gap-2">
            <div className="w-full flex items-center min-w-0 gap-0">
              <input
                type="text"
                placeholder="What are you looking for?"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={onSearchInputKeyDown}
                className="flex-1 focus:shadow-none focus:border-white h-[44px] px-4 rounded-[8px] rounded-r-none bg-white text-_1424 font-work-sans font-normal placeholder-mqgrey3 min-w-0"
              />
              <div className="w-[180px] -ml-2 relative z-10">
                <SearchDropdown
                  value={selectedSearchBrand}
                  onChange={setSelectedSearchBrand}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={onSearchSubmit}
              className="w-full tablet:w-auto px-5 py-2 bg-mqgrey4 text-white text-_1424 font-work-sans font-medium rounded-[8px] hover:bg-mqgrey5 transition-colors whitespace-nowrap"
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* DESKTOP: Secondary Navigation - White bar with category dropdown */}
      <div className="hidden desktop-lg:flex w-full items-center justify-center bg-white border-b border-mqgrey1 shadow-shadow-1 container-full">
        <div className="w-full max-w-[1136px] flex items-center justify-between h-[56px] relative">
          {/* Category Dropdown */}
          <div ref={categoryMenuRef} className="relative">
            <button
              onClick={() => setIsCategoryMenuOpen(!isCategoryMenuOpen)}
              className="flex items-center gap-[8px] px-[16px] py-[8px] rounded-[6px] hover:bg-mqgrey1 transition-colors h-[40px]"
            >
              <ListCategoryIcon className="w-[24px] h-[24px] fill-none stroke-current stroke-1" />
              <span className="text-_1424 font-work-sans font-medium text-mqgrey5">Category</span>
              <ArrowDownIcon className={`text-mqgrey5 transition-transform ${isCategoryMenuOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Catalog Link */}
          <a
            href={PRODUCT_CATALOGUE_URL}
            download
            className="flex items-center gap-[8px] text-_1424 font-work-sans font-normal text-mqgrey5 hover:text-mqred7 transition-colors"
          >
            <BookOpen className="w-[24px] h-[24px] fill-none stroke-current stroke-1" />
            <span>Click here to Download the MQ Products Catalogue</span>
          </a>

          {/* Right Section */}
          <div className="flex items-center gap-[32px]">
            <Link to="/account/orders" className="flex items-center gap-[8px] text-_1424 font-work-sans font-normal text-mqgrey5 hover:text-mqred7 transition-colors">
              <TruckIcon className="w-[24px] h-[24px] fill-none" />
              <span>Track Your Order</span>
            </Link>
            <Link to={accountLinkPath} className="flex items-center gap-[8px] text-_1424 font-work-sans font-normal text-mqgrey5 hover:text-mqred7 transition-colors">
              <UserIcon className="w-[24px] h-[24px] fill-none" />
              <span>{accountLinkLabel}</span>
            </Link>
          </div>

          {/* Mega Menu */}
          <div ref={megaMenuRef} className="absolute left-0 right-0 top-full">
            <MegaMenu isOpen={isCategoryMenuOpen} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
